import { useEffect, useState } from 'react';
import { useRouteError } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';

// Vite genera archivos con hash por build. Si el navegador tiene abierta una
// versión vieja del index.html (o el chunk fue reemplazado por un deploy)
// el import() de una ruta lazy falla con este mensaje.
const CHUNK_LOAD_ERROR = /dynamically imported module|importing a module script failed/i;
const RELOAD_FLAG = 'senciyo:route-reload-attempted';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : 'Error desconocido';
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const message = getErrorMessage(error);
  const isChunkLoadError = CHUNK_LOAD_ERROR.test(message);
  const [reloadFailed, setReloadFailed] = useState(false);

  useEffect(() => {
    if (!isChunkLoadError) return;

    if (sessionStorage.getItem(RELOAD_FLAG)) {
      sessionStorage.removeItem(RELOAD_FLAG);
      setReloadFailed(true);
      return;
    }

    sessionStorage.setItem(RELOAD_FLAG, '1');
    window.location.reload();
  }, [isChunkLoadError]);

  if (isChunkLoadError && !reloadFailed) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-red-50 px-6 py-4 border-b border-red-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-red-900">Algo salió mal</h1>
              <p className="text-sm text-red-700">
                {isChunkLoadError
                  ? 'No pudimos cargar la última versión de la aplicación.'
                  : 'Ocurrió un error inesperado en la aplicación.'}
              </p>
            </div>
          </div>
        </div>
        <div className="px-6 py-4">
          <p className="text-sm text-gray-600">
            Recarga la página para continuar. Si el problema persiste, contacta al soporte técnico.
          </p>
        </div>
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            <RefreshCw className="w-4 h-4" />
            Recargar página
          </button>
        </div>
      </div>
    </div>
  );
}
